/**
 * A planned piece → the payload(s) its host will send. The ONLY place a format is chosen.
 *
 * The choice needs no host knowledge: it asks the transport what the destination `accepts` and
 * whether a rasteriser was supplied. A text template goes out as token text or as bytes; the
 * multi-language template needs bytes AND a rasteriser, and without both it fails as
 * `UNSUPPORTED_TEMPLATE` — v1 mobile pushed that HTML through the tokenizer and printed garbage.
 */
import md5 from "md5";

import { HTML_PAGE_BREAK, isHtmlTemplate, renderBillContent } from "./content";
import { encodeEscPos } from "./encode/escpos";
import { encodeLines } from "./encode/lines";
import { encodeRaster } from "./encode/raster";
import type { ContentType, Outcome, Payload, PayloadFormat, Piece, Rasterizer } from "./types";

export type Rendered = { payloads: Payload[] } | { error: Outcome };

/** v1 renders an HTML page at 12px per character of the printer's width. */
const PX_PER_CHAR = 12;

/** Electron's main process treats a printer saved without a content type as FORMATTED. */
function contentTypeOf(piece: Piece): ContentType {
  const saved = piece.destination.to === "printer" ? piece.destination.printer?.contentType : undefined;
  return (saved as ContentType) || "FORMATTED";
}

function textPayload(text: string, piece: Piece, accepts: readonly PayloadFormat[]): Rendered {
  const format = accepts.find((f) => f === "escpos-text" || f === "escpos-bytes");
  const contentType = contentTypeOf(piece);
  if (format === "escpos-text") return { payloads: [{ format, text, contentType }] };
  if (format === "escpos-bytes") return { payloads: [{ format, bytes: encodeEscPos(contentType, text) }] };
  return { error: { ok: false, code: "UNSUPPORTED_FORMAT" } };
}

/**
 * v1 `getCustomQRData`: a Snaplion QR carries the bill number and amount signed with the
 * provider's secret. Only the HTML path draws a custom QR — v1's text path never set one.
 */
export function customQrData(sale: any, params: any): string {
  const provider = params?.billTemplate?.qrProviderData;
  if (!sale?.printQRCode || provider?.type !== "snaplion" || !provider.id || !provider.appId || !provider.appSecret) {
    return "";
  }
  const data = [sale.billNumber, sale.billRoundedAmount, provider.appId, provider.id].join(",");
  return data + "," + md5(data + provider.appSecret);
}

/** v1 `updateQrAndBarcodeData`: an HTML template cannot call back into the library for codes. */
async function withCodeImages(sale: any, params: any, raster: Rasterizer): Promise<any> {
  const data = { ...sale };
  const custom = customQrData(data, params);
  if (custom) data.billQRCode = await raster.qrDataUrl(custom);
  else if (data.url && data.printQRCode) data.billQRCode = await raster.qrDataUrl(data.url);
  if (data.sourceInfo?.source === "zomato" && data.sourceInfo.invoiceNumber) {
    data.billBarcodeImage = await raster.barcodeDataUrl(data.sourceInfo.invoiceNumber);
  }
  return data;
}

export async function renderPiece(piece: Piece, accepts: readonly PayloadFormat[], raster?: Rasterizer): Promise<Rendered> {
  const { source } = piece;
  if (source.kind === "text") return textPayload(source.text, piece, accepts);

  // A Pine Labs terminal lays the receipt out itself, from the SALE — not from the library's
  // content. It is the destination's only format, so it is chosen before anything is rendered.
  if (accepts[0] === "lines") return { payloads: [{ format: "lines", lines: encodeLines(source.data) }] };

  if (!isHtmlTemplate(source.params)) return textPayload(renderBillContent(source.data, source.params), piece, accepts);

  if (!raster || !accepts.includes("escpos-bytes")) return { error: { ok: false, code: "UNSUPPORTED_TEMPLATE" } };
  const data = await withCodeImages(source.data, source.params, raster);
  const pages = renderBillContent(data, source.params)
    .split(HTML_PAGE_BREAK)
    .filter((page) => page && page.length > 0);
  // v1 defaults only a MISSING width — `getPrintDataFromHtml(sale, params, printer?.printWidth)`
  const maxChars = piece.destination.to === "printer" ? piece.destination.printer?.printWidth : undefined;
  const widthPx = Math.floor(PX_PER_CHAR * Number(maxChars === undefined ? 46 : maxChars));

  const payloads: Payload[] = [];
  for (const page of pages) {
    const { width, height, rgba } = await raster.toPixels(page, widthPx);
    payloads.push({ format: "escpos-bytes", bytes: encodeRaster(rgba, width, height) });
  }
  return { payloads };
}
